import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useRef, useState } from "react";
import { CustomCalendar } from "../../../components/CustomCalendar/CustomCalendar";
import { PullToRefresh } from "../../../components/UIToolkit/PullToRefresh/PullToRefresh";
import { WeekStartDaySelector } from "../../../components/WeekStartDaySelector/WeekStartDaySelector";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { NEUTRAL } from "../../../theme/tokens";
import type { OpenShift, TransformedShift } from "../../../utils/models";
import { ShiftWeekListMedical } from "./medicalDepartment/ShiftWeekListMedical";
import { ShiftWeekList } from "./ShiftWeekList";

type NurseScheduleSectionProps = {
	shifts: TransformedShift[];
	allOpenShifts?: Record<string, OpenShift[]>;
	staffScheduleDisplayUntilDate?: DateTime;
	onRefresh?: () => Promise<void>;
	children?: ReactNode;
};

export const NurseScheduleSection: FC<NurseScheduleSectionProps> = ({ shifts, allOpenShifts, staffScheduleDisplayUntilDate, onRefresh, children }) => {
	const { isMedicalDepartment } = useDepartmentFlags();
	const { track } = useAnalytics();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
	const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(true);
	const [expandedShiftIds, setExpandedShiftIds] = useState<string[]>([]);
	const [calendarHeaderHeight, setCalendarHeaderHeight] = useState<number>(0);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const childrenContainerRef = useRef<HTMLDivElement | null>(null);

	const handleDateSelect = useCallback(
		(date: string): void => {
			setSelectedDate(DateTime.fromISO(date));
			setExpandedShiftIds([]);
			setIsCalendarOpen(true);
			track("nurse_schedule_date_selected", { date });
		},
		[track],
	);

	const handleExpandClick = useCallback((shiftId: string): void => {
		setExpandedShiftIds((prev) => (prev.includes(shiftId) ? prev.filter((id) => id !== shiftId) : [...prev, shiftId]));
	}, []);

	const handleCalendarChange = useCallback(
		(newDate: DateTime) => {
			setSelectedDate((prev) => {
				track("nurse_schedule_calendar_date_changed", {
					previous_date: prev.toFormat("yyyy-MM-dd"),
					new_date: newDate.toFormat("yyyy-MM-dd"),
				});
				return newDate;
			});
		},
		[track],
	);

	const handleOpenChange = useCallback(
		(isOpen: boolean) => {
			setIsCalendarOpen(isOpen);
			track("nurse_schedule_calendar_toggle", { is_open: isOpen });
		},
		[track],
	);

	const openShifts = allOpenShifts ?? {};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", gap: 2 }}>
			{isDynamicWeekStartEnabled && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
					<WeekStartDaySelector source="staffSchedule" />
				</Box>
			)}
			<CustomCalendar
				shifts={shifts}
				onRootHeightChange={setCalendarHeaderHeight}
				value={selectedDate}
				onChange={handleCalendarChange}
				isOpen={isCalendarOpen}
				onOpenChange={handleOpenChange}
				showOneWeek={false}
				allowWeeksExpansion={true}
			/>

			<Box
				ref={scrollRef}
				id={"scrollable-nurse-schedule-container"}
				style={{
					overflowY: "auto",
					overscrollBehaviorY: "contain",
					WebkitOverflowScrolling: "touch",
					background: NEUTRAL[100],
				}}
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: `calc(100% - ${calendarHeaderHeight}px)`,
					position: "relative",
					p: 1,
					py: 0,
				}}
			>
				{onRefresh && <PullToRefresh childrenContainerEl={childrenContainerRef.current} scrollerEl={scrollRef.current} onRefresh={onRefresh} />}
				<Box ref={childrenContainerRef} id="nurse-schedule-content-box" data-testid="nurse-schedule-shift-list">
					{isMedicalDepartment ? (
						<ShiftWeekListMedical
							selectedDate={selectedDate.toFormat("yyyy-MM-dd")}
							shifts={shifts}
							allOpenShifts={openShifts}
							staffScheduleDisplayUntilDate={staffScheduleDisplayUntilDate}
						/>
					) : (
						<ShiftWeekList
							shifts={shifts}
							selectedDate={selectedDate.toFormat("yyyy-MM-dd")}
							onDateSelect={handleDateSelect}
							expandedShiftIds={expandedShiftIds}
							onExpandOpenShift={handleExpandClick}
							allOpenShifts={openShifts}
							staffScheduleDisplayUntilDate={staffScheduleDisplayUntilDate}
						/>
					)}
					{children}
				</Box>
			</Box>
		</Box>
	);
};
